import React from "react";

import { DespatchCrew } from "Models/despatch.crew";
import { DespatchTask } from "Models/despatch.task";
import { Task } from "Models/task";
import { TFunction } from "react-i18next";
import { differenceInDays, startOfDay, startOfToday } from "date-fns";

import { checkAvailability } from "./checkAvailability";
import { TaskStatus } from "./enums";

const isTaskEnabledToCancel = (task: DespatchTask | Task) => {
  if (task?.isPlaceholder) return false;

  return [
    TaskStatus.REGISTERED,
    TaskStatus.WAITING_PUBLISH,
    TaskStatus.PENDING_ACCEPT,
    TaskStatus.TO_DO,
  ].includes(task.status as TaskStatus);
};

const isPlaceholderTaskEnabledToDelete = (task: DespatchTask | Task) => {
  return !!task?.isPlaceholder;
};

const isTaskEnabledToSeeMoreOptions = (task: DespatchTask | Task) => {
  return !task?.isPlaceholder;
};

const isTaskEnabledToTimeControl = (task: DespatchTask | Task) => {
  return !task?.isPlaceholder;
};

const isTaskDisabledToActions = (task: DespatchTask | Task) => {
  return [TaskStatus.CANCELLED].includes(task.status as TaskStatus);
};

type TaskEnableToPublishCrew = Partial<Pick<DespatchCrew, "memberLeader" | "members" | "fleets">>;

const isCrewTaskEnableToPublish = (task: DespatchTask | Task, crew: TaskEnableToPublishCrew) => {
  const statusAvailable = [TaskStatus.REGISTERED, TaskStatus.WAITING_PUBLISH].includes(
    task.status as TaskStatus
  );
  const hasLeader = !!crew?.memberLeader?.id;

  const taskDate = new Date(task.startDate);
  const leaderIsAvailable = checkAvailability(
    taskDate,
    crew?.memberLeader?.availabilities
  ).isAvailable;

  const membersAvailable =
    crew.members?.every((member) => {
      return checkAvailability(taskDate, member.availabilities).isAvailable;
    }) ?? true;

  const fleetsAvailable =
    crew.fleets?.every((fleet) => {
      return checkAvailability(taskDate, fleet.availabilities).isAvailable;
    }) ?? true;

  return statusAvailable && hasLeader && leaderIsAvailable && membersAvailable && fleetsAvailable;
};

const getTaskPublishStatus = (task: DespatchTask | Task) => {
  if ([TaskStatus.PENDING_ACCEPT, TaskStatus.ACCEPTED].includes(task.status as TaskStatus))
    return "unpublish";

  if ([TaskStatus.REGISTERED, TaskStatus.WAITING_PUBLISH].includes(task.status as TaskStatus))
    return "publish";
};

const isTaskEnableToUnpublish = (task: DespatchTask | Task) => {
  return [TaskStatus.PENDING_ACCEPT, TaskStatus.ACCEPTED].includes(task.status as TaskStatus);
};

const isTaskEnabledToRemoveFromTeam = (task: DespatchTask | Task) => {
  if (
    [
      TaskStatus.PENDING_ACCEPT,
      TaskStatus.STARTED,
      TaskStatus.FINISHED,
      TaskStatus.CANCELLED,
    ].includes(task.status as TaskStatus)
  ) {
    return false;
  }

  return true;
};

const isTaskFinished = (task: DespatchTask | Task) => {
  return TaskStatus.FINISHED === (task.status as TaskStatus);
};

const isTaskEnableToPublish = (task: DespatchTask) => {
  const statusAvailable = [TaskStatus.REGISTERED, TaskStatus.WAITING_PUBLISH].includes(
    task.status as TaskStatus
  );
  const team = task.teams?.find((t) => {
    return differenceInDays(startOfToday(), startOfDay(new Date(t.createdTo!)));
  });

  const hasLeader = !!team?.memberLeader?.id;

  const taskDate = new Date(task.startDate);
  const leaderIsAvailable = checkAvailability(
    taskDate,
    team?.memberLeader?.availabilities
  ).isAvailable;

  const membersAvailable =
    team?.members?.every((member) => {
      return checkAvailability(taskDate, member.availabilities).isAvailable;
    }) ?? true;

  const fleetsAvailable =
    team?.fleets?.every((fleet) => {
      return checkAvailability(taskDate, fleet.availabilities).isAvailable;
    }) ?? true;

  return statusAvailable && hasLeader && leaderIsAvailable && membersAvailable && fleetsAvailable;
};

export const checkPublishEnabledTaskStatus = (task: DespatchTask) => {
  if (task?.isPlaceholder) return false;
  const isUnpublish = isTaskEnableToUnpublish(task);

  if (isUnpublish) return "unpublish";

  const isPublish = isTaskEnableToPublish(task);

  if (isPublish) return "publish";

  return false;
};

const getPublishOrUnpublishOption = (
  task: DespatchTask,
  crew: DespatchCrew,
  translate: TFunction<"translation", undefined>,
  IconPublish: any,
  IconUnpublish: any,
  onClick: () => void,
  isLoading: boolean
) => {
  const isUnpublish = isTaskEnableToUnpublish(task);

  if (isUnpublish) {
    return {
      label: translate("unpublishTask"),
      leftContent: <IconUnpublish size={24} />,
      onClick: () => onClick(),
      isLoading,
    };
  }

  const isPublish = isCrewTaskEnableToPublish(task, crew);

  let tooltipMessage = "";

  if (!isPublish) {
    tooltipMessage = translate("youCannotPublishThisTask");
  }

  if (!crew?.memberLeader?.id) {
    tooltipMessage = translate("youCannotPublishATaskWithoutALeader");
  }

  return {
    label: translate("publishTask"),
    leftContent: <IconPublish size={24} />,
    onClick: () => onClick(),
    isLoading,
    isDisabled: !isPublish,
    tooltipMessage,
  };
};

const isCrewEnabledToRemoveLeader = (crew: DespatchCrew) => {
  if (!crew?.tasks?.length) return true;

  const statusTasks = crew.tasks
    .map((task) => task.status)
    .filter((status) => status.toLowerCase() === TaskStatus.REGISTERED.toLowerCase());

  if (statusTasks.length) return true;

  return false;
};

const isCrewEnabledToEditResourcesAndLeader = (
  crew: DespatchCrew,
  currentDate: Date | string
): boolean => {
  const isDraft = isCrewEnabledToRemoveLeader(crew);

  if (isDraft) return true;

  const startTasks = crew.tasks
    .map((task) => ({ startDate: task.startDate, status: task.status }))
    .filter((task) => task.status.toLowerCase() !== TaskStatus.REGISTERED.toLowerCase())
    .filter((task) => {
      return (
        Math.abs(differenceInDays(startOfDay(new Date(task.startDate)), new Date(currentDate))) > 0
      );
    });

  if (startTasks?.length) return true;

  return false;
};

export {
  isTaskEnabledToCancel,
  isPlaceholderTaskEnabledToDelete,
  isTaskEnabledToSeeMoreOptions,
  isTaskEnabledToTimeControl,
  isTaskDisabledToActions,
  isCrewTaskEnableToPublish,
  isTaskEnableToUnpublish,
  isTaskEnabledToRemoveFromTeam,
  getPublishOrUnpublishOption,
  getTaskPublishStatus,
  isCrewEnabledToRemoveLeader,
  isTaskFinished,
  isCrewEnabledToEditResourcesAndLeader,
};
